import React, { Component, Fragment, useEffect } from 'react';
import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { Cell, Grid, GridContainer } from 'formlabs-web-components/Grid';
import { DisplayComponent } from 'formlabs-web-components/BaseComponents';
import {
  Accordion,
  AccordionItem,
  CarouselWrapper as Carousel,
  Picture
} from 'formlabs-web-components/Media';
import { Header } from 'formlabs-web-components/Type';
import { Breadcrumbs, StoreMenu } from '../navigation';

import { formatPrice } from '../../utils';

import AddToCart from './AddToCart';
import Amount from './Amount';
import LeadTime from './LeadTime';
import Price from './Price';
import ProductOption from './ProductOption';
import UpsellProducts from './UpsellProducts';

import { analytics } from '../../utils';

import './ProductDetailPlugin.module.css';

const cdnUrl = `${
  process.env.RAZZLE_MAGENTO_MEDIA_URL
}/catalog/product/600x600`;

class ProductConfigurator extends Component {
  constructor(props) {
    super(props);

    this.state = this.getInitialState(props);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.sku !== this.props.sku) {
      this.setState(this.getInitialState(this.props));
    }
  }

  getInitialState(props) {
    const { bundleOptions } = props;

    let availableOptions = [];
    const defaultOptions = new Map();
    if (bundleOptions) {
      const groupedOptions = groupBy(
        bundleOptions,
        option =>
          option.product_links.length > 1 ||
          (option.product_links.length === 1 && option.required === false)
      );

      // not configurable bundle options, we hide them
      if (groupedOptions['false']) {
        for (let entry of groupedOptions['false']) {
          defaultOptions.set(
            entry.option_id,
            new Map([[entry.product_links[0].sku, entry.product_links[0]]])
          );
        }
      }

      // configurable bundle options
      if (groupedOptions['true']) {
        availableOptions = groupedOptions['true'];
      }
    }

    // calculate sums for all preselected options
    const optionSums = new Map();
    availableOptions.forEach(option => {
      const selectedOptions = new Map();
      const defaultItem = option.product_links.find(item => item.is_default);
      if (defaultItem) {
        selectedOptions.set(defaultItem.sku, defaultItem);
      }
      const optionSum = this.getOptionSum(selectedOptions);
      optionSums.set(option.option_id, optionSum);
    });

    const amount = 1;

    return {
      amount,
      price: this.getPrice(this.getOptionsSum(optionSums), amount),
      optionSums,
      bundle: new Map(defaultOptions),
      availableOptions
    };
  }

  getOptionsSum = optionSums => {
    return Array.from(optionSums.values()).reduce(
      (sum, price) => sum + price,
      0
    );
  };

  getPrice = (optionsSum, amount) => {
    return (this.getBasePrice() + optionsSum) * amount;
  };

  updatePrice = () => {
    this.setState({
      price: this.getPrice(
        this.getOptionsSum(this.state.optionSums),
        this.state.amount
      )
    });
  };

  getBasePrice = () => {
    const { bundleOptions, price } = this.props;

    if (!bundleOptions) {
      return price;
    }

    return bundleOptions
      .filter(
        option =>
          option.product_links.length <= 1 &&
          (option.product_links.length === 1 && option.required === true)
      )
      .map(option => option.product_links)
      .reduce((sum, products) => {
        const price = products.reduce(
          (productSum, product) => productSum + product.price,
          0
        );
        return sum + price;
      }, 0);
  };

  handleAmountChange = amount => {
    this.setState({ amount }, this.updatePrice);
  };

  getOptionSum = selectedOptions => {
    return Array.from(selectedOptions.values()).reduce(
      (sum, option) => sum + option.price,
      0
    );
  };

  handleOptionChange = (optionId, selectedOptions) => {
    const optionSum = this.getOptionSum(selectedOptions);
    const optionSums = this.state.optionSums.set(optionId, optionSum);
    const price = this.getPrice(
      this.getOptionsSum(optionSums),
      this.state.amount
    );

    this.setState({
      optionSums,
      price,
      bundle: this.state.bundle.set(optionId, selectedOptions)
    });
  };

  priceFormatter = price =>
    formatPrice(price, this.props.language, this.props.currency);

  render() {
    const { attributes, bundleOptions, name, sku } = this.props;
    const { amount, price, bundle, availableOptions } = this.state;

    return (
      <Fragment>
        {bundleOptions && (
          <Accordion expandAll>
            {availableOptions.map(option => {
              return (
                <AccordionItem headline={option.title} key={option.option_id}>
                  <ProductOption
                    option={option}
                    onOptionChange={this.handleOptionChange}
                    priceFormatter={this.priceFormatter}
                  />
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
        {attributes.leadTime && attributes.leadTime > 0 ? (
          <LeadTime value={attributes.leadTime} />
        ) : null}
        <Price styleName="price" price={price} />
        <div styleName="actions">
          <AddToCart
            amount={amount}
            sku={sku}
            name={name}
            bundleOptions={bundle}
          />
          <div styleName="amount">
            <Amount onAmountChange={this.handleAmountChange} />
          </div>
        </div>
      </Fragment>
    );
  }
}

const ProductDetailPlugin = props => {
  const [t] = useTranslation();
  const {
    attributes,
    baseUrl,
    category,
    media,
    menu,
    name,
    sku,
    bundleOptions,
    productLinks
  } = props;
  const { description } = attributes;

  const navigationEntries = [{ name: t('Store'), url: baseUrl }];
  useEffect(() => {
    analytics.track('Product Viewed', {
      sku: sku,
      category: category,
      name: name,
      brand: 'Formlabs',
      quantity: 1
    });
  });
  if (category) {
    navigationEntries.push({
      name: category.name,
      url: `${baseUrl}${category.attributes.urlPath}/`
    });
  }

  if (name) {
    navigationEntries.push({ name });
  }

  const pictures = media
    ? media.filter(item => item.media_type === 'image')
    : [];

  const upsellProducts = productLinks
    ? sortBy(
        productLinks
          .filter(link => link.link_type === 'related')
          .map(link => link.linked_product_sku),
        ['position']
      )
    : null;
  const hasUpsell = Boolean(upsellProducts && upsellProducts.length);

  return (
    <Fragment>
      <div styleName={classnames('page', { 'no-upsell': !hasUpsell })}>
        <StoreMenu baseUrl={baseUrl} menu={menu} />
        <div styleName="menu">
          <Breadcrumbs entries={navigationEntries} />
        </div>
        <GridContainer>
          <Header level={1} displayAs={1} weight={700}>
            {name}
          </Header>
          <p styleName="sku">{sku}</p>
          <Grid direction="x" gutters="margin">
            <Cell mediumSize="12" smallOrder="2" mediumOrder="1">
              <div styleName="images">
                {pictures.length > 1 ? (
                  <Carousel controlsPosition="bottom">
                    {pictures.map(picture => (
                      <Picture
                        fullBleed
                        key={picture.id}
                        largeImage={`${cdnUrl}${picture.file}`}
                      />
                    ))}
                  </Carousel>
                ) : pictures.length ? (
                  <Picture
                    fullBleed
                    key={pictures[0].id}
                    largeImage={`${cdnUrl}${pictures[0].file}`}
                    alt={t('Product image for {{name}}', { name: name })}
                  />
                ) : null}
              </div>
              {bundleOptions && (
                <div
                  styleName="long-desc"
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              )}
            </Cell>
            <Cell mediumSize="12" smallOrder="1" mediumOrder="2">
              {!bundleOptions && (
                <div
                  styleName="long-desc"
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              )}
              <ProductConfigurator {...props} />
            </Cell>
          </Grid>
        </GridContainer>
      </div>
      {hasUpsell ? (
        <div styleName="upsell">
          <GridContainer>
            <UpsellProducts skus={upsellProducts}>
              <div styleName="upsell-header">
                <Header level={2} displayAs={3} weight={600}>
                  {t('You may also like')}
                </Header>
              </div>
            </UpsellProducts>
          </GridContainer>
        </div>
      ) : null}
    </Fragment>
  );
};

export default compose(
  connect(state => {
    return {
      language: state.i18n.language,
      currency: state.i18n.currency
    };
  }),
  DisplayComponent
)(ProductDetailPlugin);
